import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  Typography,
  Box,
  Chip,
  Stack,
  IconButton,
  Divider,
  Checkbox,
  Alert,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { Close } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { offers, listings, ListingType } from '@prisma/client';
import { OfferRoundWithRelations } from '../../api/services/offerRounds';
import useIsMobile from '../../hooks/useIsMobile';
import { formatCurrency } from '../../utils/formatCurrency';

/**
 * Opening values for a counter the buyer did not type from scratch — Reva's suggested
 * terms on the Counter Decision card. Seeded on mount, then fully editable; the "Was:"
 * diff still measures against the round the buyer is answering, not against these.
 */
export interface NegotiationPrefill {
  price?: number;
  closingDate?: Dayjs;
  earnestMoney?: number;
  concessions?: number;
  appliances?: boolean;
  inspectionPeriod?: number;
  contingent?: boolean;
  bagentComm?: number;
  loan?: number;
  homeWarrantyType?: string;
  termiteInspection?: boolean;
  termiteBond?: boolean;
  sewerInspection?: boolean;
  newSurvey?: boolean;
  paymentMethod?: string;
  additionalTerms?: string;
}

interface NegotiationFormProps {
  open: boolean;
  onClose: () => void;
  offer: offers & { currentRound?: OfferRoundWithRelations };
  lastRound?: OfferRoundWithRelations;
  onSubmit: (data: any) => Promise<void>;
  mode: 'counter' | 'accept' | 'reject';
  token?: string;
  isBuyer?: boolean;
  isPublicView?: boolean;
  listing?: listings;
  prefill?: NegotiationPrefill;
}

const validationSchema = Yup.object({
  price: Yup.number()
    .required('Price is required')
    .positive('Price must be positive'),
  closingDate: Yup.date()
    .required('Closing date is required')
    .min(new Date(), 'Closing date must be in the future'),
  earnestMoney: Yup.number().min(0, 'Earnest money cannot be negative'),
  concessions: Yup.number().min(0, 'Concessions cannot be negative'),
  inspectionPeriod: Yup.number(),
  loan: Yup.number().min(0, 'Loan amount cannot be negative'),
  bagentComm: Yup.number()
    .min(0, 'Commission cannot be negative')
    .max(10, 'Commission seems too high'),
  paymentMethod: Yup.string().required('Payment method is required'),
});

export const NegotiationForm: React.FC<NegotiationFormProps> = ({
  open,
  onClose,
  offer,
  lastRound,
  onSubmit,
  mode,
  isBuyer = false,
  isPublicView = false,
  listing,
  prefill,
}) => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
  const [termsConfirmed, setTermsConfirmed] = useState(false);

  // Reset confirmation when dialog opens/closes or mode changes
  React.useEffect(() => {
    if (open) {
      setTermsConfirmed(false);
    }
  }, [open, mode]);

  // Use the round's actual column values as the source of truth for current terms.
  // Don't use changedFields for values — round columns already store the full state.
  const currentTerms = React.useMemo(
    () =>
      lastRound
        ? {
            ...offer,
            ...lastRound,
          }
        : offer,
    [lastRound, offer]
  );

  const formik = useFormik({
    initialValues: {
      price: currentTerms.price || 0,
      closingDate: dayjs(currentTerms.closingDate),
      earnestMoney: currentTerms.earnestMoney || 0,
      concessions: currentTerms.concessions || 0,
      appliances: currentTerms.appliances || false,
      inspectionPeriod: currentTerms.inspectionPeriod || 10,
      contingent: currentTerms.contingent || false,
      bagentComm: currentTerms.bagentComm || 2.5,
      loan: currentTerms.loan || 0,
      homeWarrantyType: currentTerms.homeWarrantyType || 'NONE',
      termiteInspection: currentTerms.termiteInspection || false,
      termiteBond: currentTerms.termiteBond || false,
      sewerInspection: currentTerms.sewerInspection || false,
      newSurvey: currentTerms.newSurvey || false,
      additionalTerms: currentTerms.additionalTerms || '',
      paymentMethod: currentTerms.paymentMethod || 'Cash',
      responseNotes: '',
      ...(prefill ?? {}),
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const submitData = {
          ...values,
          offerId: offer.id,
          closingDate: values.closingDate.toDate(),
          changedFields: Array.from(changedFields),
        };
        await onSubmit(submitData);
        onClose();
      } catch (error) {
        console.error('Error submitting negotiation:', error);
      } finally {
        setLoading(false);
      }
    },
  });

  // Track field changes
  useEffect(() => {
    const checkChanges = () => {
      const changes = new Set<string>();
      // Determine what to compare against based on context
      const compareAgainst = lastRound ? currentTerms : offer;

      Object.keys(formik.values).forEach((key) => {
        if (key === 'responseNotes' || key === 'mode') return;

        // Only track bagentComm changes if listing type is SELLER
        if (key === 'bagentComm' && listing?.type !== ListingType.SELLER) {
          return;
        }

        let currentValue = (formik.values as Record<string, any>)[key];
        let originalValue = (compareAgainst as Record<string, any>)[key];

        if (key === 'closingDate') {
          currentValue = currentValue?.format('YYYY-MM-DD');
          originalValue = dayjs(originalValue).format('YYYY-MM-DD');
        }

        if (currentValue !== originalValue) {
          changes.add(key);
        }
      });
      setChangedFields(changes);
    };

    checkChanges();
  }, [formik.values, currentTerms, offer, lastRound, listing]);

  const getTitle = () => {
    switch (mode) {
      case 'counter':
        return 'Counter Offer';
      case 'accept':
        return 'Accept Offer';
      case 'reject':
        return 'Reject Offer';
      default:
        return 'Negotiate';
    }
  };

  const isFieldChanged = (fieldName: string) => changedFields.has(fieldName);

  const formatPreviousValue = (field: string, value: any): string => {
    if (value === null || value === undefined) return '';

    switch (field) {
      case 'price':
      case 'earnestMoney':
      case 'concessions':
      case 'loan':
        return `$${value.toLocaleString()}`;
      case 'closingDate':
        return new Date(value).toLocaleDateString();
      case 'appliances':
      case 'contingent':
      case 'termiteInspection':
      case 'termiteBond':
      case 'sewerInspection':
      case 'newSurvey':
        return value ? 'Yes' : 'No';
      case 'bagentComm':
        return `${value}%`;
      case 'inspectionPeriod':
        return `${value} days`;
      case 'homeWarrantyType':
        return value === 'NONE'
          ? 'None'
          : value === 'SELLER'
          ? 'Seller'
          : 'Buyer';
      case 'paymentMethod':
        return value;
      default:
        return String(value);
    }
  };

  const getPreviousValue = (field: string) => {
    // If there's a lastRound, use its value
    if (lastRound) {
      return (lastRound as any)[field];
    }
    // Otherwise, if this is the initial counter offer, use the original offer value
    if (!lastRound && mode === 'counter') {
      return (offer as any)[field];
    }
    return null;
  };

  const renderChangeIndicator = (field: string) => {
    // Show change indicator if field has changed AND either:
    // 1. There's a lastRound (comparing to previous negotiation)
    // 2. This is the initial counter (comparing to original offer)
    if (!isFieldChanged(field)) return null;
    if (!lastRound && mode !== 'counter') return null;

    const prevValue = getPreviousValue(field);
    const formattedValue = formatPreviousValue(field, prevValue);

    // Determine label based on whether this is comparing to original offer or last round
    const label = !lastRound
      ? `Original: ${formattedValue}`
      : `Was: ${formattedValue}`;

    return (
      <Chip
        label={label}
        size="small"
        sx={{
          mt: 1,
          fontWeight: 500,
          backgroundColor: '#fff9c4',
          color: '#6d4c41',
          border: '1px solid #f9e79f',
        }}
      />
    );
  };

  // Simple rejection confirmation dialog
  if (mode === 'reject') {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Reject Offer</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1">
              Current offer:{' '}
              <strong>{formatCurrency(currentTerms.price || 0)}</strong>
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsConfirmed}
                  onChange={(e) => setTermsConfirmed(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I confirm that I want to reject this offer. This action cannot
                  be undone.
                </Typography>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={() => formik.handleSubmit()}
            variant="contained"
            color="error"
            disabled={loading || !termsConfirmed}
          >
            {loading ? 'Rejecting...' : 'Reject Offer'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{getTitle()}</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid2 container spacing={1.5}>
              {/* Negotiation System Info */}
              {mode === 'counter' && (
                <Grid2 xs={12}>
                  <Alert
                    severity="info"
                    sx={{
                      backgroundColor: '#e3f2fd',
                      color: '#000',
                      '& .MuiAlert-icon': {
                        color: '#1976d2',
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 1, color: '#000' }}
                    >
                      How Homepoint Negotiations Work
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#000' }}>
                      Our digital negotiation platform streamlines real estate
                      negotiations by eliminating the traditional back-and-forth
                      signing of multiple counter offer documents. Instead of
                      preparing, signing, and exchanging formal paperwork for
                      each round, parties can quickly communicate proposed terms
                      through our platform. All negotiations are preliminary
                      discussions and non-binding proposals until both parties
                      agree to terms and proceed to execute a formal purchase
                      agreement. This modern approach saves time, reduces
                      paperwork, and helps buyers and sellers reach agreement
                      faster while maintaining a clear record of all proposals
                      exchanged.
                    </Typography>
                  </Alert>
                </Grid2>
              )}
              {/* Price and Payment */}
              <Grid2 xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 0.5 }}
                >
                  Price & Payment
                </Typography>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Offer Price"
                  name="price"
                  type="number"
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  error={formik.touched.price && Boolean(formik.errors.price)}
                  helperText={
                    formik.touched.price && formik.errors.price
                      ? formik.errors.price
                      : undefined
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  disabled={mode !== 'counter'}
                />
                {renderChangeIndicator('price')}
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="payment-method-label">
                    Payment Method
                  </InputLabel>
                  <Select
                    labelId="payment-method-label"
                    id="payment-method"
                    name="paymentMethod"
                    value={formik.values.paymentMethod || ''}
                    onChange={(e) => {
                      formik.handleChange(e);
                      // Reset loan amount to 0 when switching to Cash
                      if (e.target.value === 'Cash') {
                        formik.setFieldValue('loan', 0);
                      }
                    }}
                    disabled={mode !== 'counter'}
                    label="Payment Method"
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Financed">Financed</MenuItem>
                  </Select>
                </FormControl>
                {renderChangeIndicator('paymentMethod')}
              </Grid2>

              {formik.values.paymentMethod === 'Financed' && (
                <Grid2 xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Loan Amount"
                    name="loan"
                    type="number"
                    value={formik.values.loan}
                    onChange={formik.handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    disabled={mode !== 'counter'}
                  />
                  {renderChangeIndicator('loan')}
                </Grid2>
              )}

              <Grid2 xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Earnest Money"
                  name="earnestMoney"
                  type="number"
                  value={formik.values.earnestMoney}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.earnestMoney &&
                    Boolean(formik.errors.earnestMoney)
                  }
                  helperText={
                    formik.touched.earnestMoney && formik.errors.earnestMoney
                      ? formik.errors.earnestMoney
                      : undefined
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  disabled={mode !== 'counter'}
                />
                {renderChangeIndicator('earnestMoney')}
              </Grid2>

              {/* Dates and Timeline */}
              <Grid2 xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 0.5 }}
                >
                  Timeline
                </Typography>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <DatePicker
                  label="Closing Date"
                  value={formik.values.closingDate}
                  onChange={(value) =>
                    formik.setFieldValue('closingDate', value)
                  }
                  disabled={mode !== 'counter'}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error:
                        formik.touched.closingDate &&
                        Boolean(formik.errors.closingDate),
                      helperText:
                        formik.touched.closingDate && formik.errors.closingDate
                          ? String(formik.errors.closingDate)
                          : '',
                    },
                  }}
                />
                {renderChangeIndicator('closingDate')}
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Inspection Period (days)"
                  name="inspectionPeriod"
                  type="number"
                  value={formik.values.inspectionPeriod}
                  onChange={formik.handleChange}
                  disabled={mode !== 'counter'}
                />
                {renderChangeIndicator('inspectionPeriod')}
              </Grid2>

              {/* Concessions and Commissions */}
              <Grid2 xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 0.5 }}
                >
                  Concessions & Commissions
                </Typography>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Seller Concessions"
                  name="concessions"
                  type="number"
                  value={formik.values.concessions}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.concessions &&
                    Boolean(formik.errors.concessions)
                  }
                  helperText={
                    formik.touched.concessions && formik.errors.concessions
                      ? formik.errors.concessions
                      : undefined
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  disabled={mode !== 'counter'}
                />
                {renderChangeIndicator('concessions')}
              </Grid2>

              {listing?.type === ListingType.SELLER && (
                <Grid2 xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Buyer Agent Commission"
                    name="bagentComm"
                    type="number"
                    value={formik.values.bagentComm}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.bagentComm &&
                      Boolean(formik.errors.bagentComm)
                    }
                    helperText={
                      formik.touched.bagentComm && formik.errors.bagentComm
                        ? formik.errors.bagentComm
                        : undefined
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    disabled={mode !== 'counter'}
                  />
                  {renderChangeIndicator('bagentComm')}
                </Grid2>
              )}

              {/* Additional Terms */}
              <Grid2 xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 0.5 }}
                >
                  Additional Terms
                </Typography>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Include Appliances</FormLabel>
                  <RadioGroup
                    row
                    name="appliances"
                    value={formik.values.appliances ? 'yes' : 'no'}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'appliances',
                        e.target.value === 'yes'
                      )
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={mode !== 'counter'}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={mode !== 'counter'}
                    />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  {renderChangeIndicator('appliances')}
                </Box>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Contingent on Sale</FormLabel>
                  <RadioGroup
                    row
                    name="contingent"
                    value={formik.values.contingent ? 'yes' : 'no'}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'contingent',
                        e.target.value === 'yes'
                      )
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={mode !== 'counter'}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={mode !== 'counter'}
                    />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  {renderChangeIndicator('contingent')}
                </Box>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="home-warranty-label">
                    Home Warranty
                  </InputLabel>
                  <Select
                    labelId="home-warranty-label"
                    id="home-warranty"
                    name="homeWarrantyType"
                    value={formik.values.homeWarrantyType || 'NONE'}
                    onChange={formik.handleChange}
                    disabled={mode !== 'counter'}
                    label="Home Warranty"
                  >
                    <MenuItem value="NONE">None</MenuItem>
                    <MenuItem value="SELLER">Seller Provided</MenuItem>
                    <MenuItem value="BUYER">Buyer Provided</MenuItem>
                  </Select>
                </FormControl>
                {renderChangeIndicator('homeWarrantyType')}
              </Grid2>

              <Grid2 xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Inspections
                </Typography>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Termite Inspection</FormLabel>
                  <RadioGroup
                    row
                    name="termiteInspection"
                    value={formik.values.termiteInspection ? 'yes' : 'no'}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'termiteInspection',
                        e.target.value === 'yes'
                      )
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={mode !== 'counter'}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={mode !== 'counter'}
                    />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  {renderChangeIndicator('termiteInspection')}
                </Box>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Termite Bond</FormLabel>
                  <RadioGroup
                    row
                    name="termiteBond"
                    value={formik.values.termiteBond ? 'yes' : 'no'}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'termiteBond',
                        e.target.value === 'yes'
                      )
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={mode !== 'counter'}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={mode !== 'counter'}
                    />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  {renderChangeIndicator('termiteBond')}
                </Box>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Sewer Inspection</FormLabel>
                  <RadioGroup
                    row
                    name="sewerInspection"
                    value={formik.values.sewerInspection ? 'yes' : 'no'}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'sewerInspection',
                        e.target.value === 'yes'
                      )
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={mode !== 'counter'}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={mode !== 'counter'}
                    />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  {renderChangeIndicator('sewerInspection')}
                </Box>
              </Grid2>

              <Grid2 xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">New Survey</FormLabel>
                  <RadioGroup
                    row
                    name="newSurvey"
                    value={formik.values.newSurvey ? 'yes' : 'no'}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'newSurvey',
                        e.target.value === 'yes'
                      )
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={mode !== 'counter'}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={mode !== 'counter'}
                    />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  {renderChangeIndicator('newSurvey')}
                </Box>
              </Grid2>

              <Grid2 xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Additional Terms"
                  name="additionalTerms"
                  value={formik.values.additionalTerms}
                  onChange={formik.handleChange}
                  disabled={mode !== 'counter'}
                />
                {renderChangeIndicator('additionalTerms')}
              </Grid2>

              {/* Response Notes */}
              <Grid2 xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Comments (Optional)"
                  name="responseNotes"
                  value={formik.values.responseNotes}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.responseNotes &&
                    Boolean(formik.errors.responseNotes)
                  }
                  helperText={
                    formik.touched.responseNotes && formik.errors.responseNotes
                  }
                  placeholder="Provide any additional comments "
                />
              </Grid2>

              {/* Terms Confirmation Checkbox */}
              <Grid2 xs={12}>
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsConfirmed}
                      onChange={(e) => setTermsConfirmed(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I understand that I am submitting proposed negotiation
                      terms for discussion purposes only, without the need for
                      formal signatures at this stage. These terms remain
                      non-binding proposals until all parties execute a formal
                      purchase agreement.
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              </Grid2>
            </Grid2>
          </DialogContent>

          <DialogActions sx={{ p: 1.5 }}>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                loading ||
                (mode === 'counter' && changedFields.size === 0) ||
                !termsConfirmed
              }
              color="primary"
            >
              {loading ? 'Processing...' : getTitle()}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};
